// Datos de la pantalla de Tiendas: la tabla del periodo, la ficha, la serie semanal y las ventas.
import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { storesTableApi } from '@/api/services/retail-wizard/backoffice';
import type { StoreDetailResponse } from '@/api/services/retail-wizard/backoffice';
import { storeActivityApi } from '@/api/services/retail-org/backoffice';
import type { KpiWindow, StoreActivityResponse } from '@/api/services/retail-org/backoffice';
import { backofficeSalesApi } from '@/api/services/retail-wizard/retail-wizard.client';
import type { BackofficeSale } from '@/api/services/retail-wizard/contract';
import { mxDay } from '@/lib/dateHelpers';
import {
    reportPeriodRange,
    scaleMonthlyGoal,
    type ReportPeriod,
} from '@/shared/patterns/PeriodBand';
import { useGoals } from '../../lib/goals';
import { zoneNameById } from '../../lib/roster';
import {
    useRosterRetailer,
    useRosterStores,
    useRosterUsers,
    useRosterZones,
} from '../../lib/useRoster';
import { isMissingEndpoint } from '../../clientes/api/useCustomers';
import { buildStoreRows, fromTableRow, type StoreRow } from '../lib/storesTable';
import { TREND_WEEKS, weeklyWindows } from '../lib/storeDetail';
import { usePeriodWindow } from './useTiendas';

const KEY = ['retail-admin', 'tiendas'] as const;
const TABLE_KEY = [...KEY, 'table'] as const;
const ACTIVITY_KEY = [...KEY, 'activity'] as const;

/** Páginas máximas del listado de ventas de la tienda; pasado el tope la ficha avisa. */
const MAX_SALES_PAGES = 20;
const SALES_PAGE_SIZE = 50;

function activityQuery(kpiWindow: KpiWindow, staleTime = 60_000) {
    return {
        queryKey: [...ACTIVITY_KEY, kpiWindow],
        queryFn: () => storeActivityApi.listStoreActivity(kpiWindow),
        staleTime,
    };
}

/** Las ventanas semanales del día; la clave no cambia hasta mañana. */
function useWeeklyWindows() {
    const today = mxDay(new Date().toISOString());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `today` rehace las ventanas al cambiar el día
    return useMemo(() => weeklyWindows(TREND_WEEKS), [today]);
}

export interface StoresTableData {
    items: StoreRow[];
    truncated: boolean;
    /** El padrón de usuarios respondió: «Equipo» y «Sin equipo activo» tienen dato. */
    teamAvailable: boolean;
    /** La ventana semanal respondió: «Δ vs sem ant» tiene dato. */
    weekAvailable: boolean;
    /** `fallback` = el endpoint nuevo no existe todavía y se cruzaron las fuentes en el front. */
    source: 'table' | 'fallback';
}

/**
 * La tabla del periodo en una llamada a `GET /backoffice/stores/table`. Si la ruta no existe (aún
 * sin desplegar) cae al cruce viejo; ese respaldo se borra cuando el endpoint esté en dev.
 */
export function useStoresTable(period: ReportPeriod) {
    const periodWindow = usePeriodWindow(period);
    const table = useQuery({
        queryKey: [...TABLE_KEY, periodWindow],
        queryFn: () => storesTableApi.getTable(periodWindow),
        staleTime: 60_000,
        retry: (failures, error) => !isMissingEndpoint(error) && failures < 2,
    });
    const useFallback = isMissingEndpoint(table.error);
    const legacy = useLegacyStoresTable(period, useFallback);

    const data = useMemo((): StoresTableData | undefined => {
        if (table.data) {
            return {
                items: table.data.items.map(fromTableRow),
                truncated: table.data.truncated,
                teamAvailable: table.data.sources.team === 'OK',
                weekAvailable: true,
                source: 'table',
            };
        }
        return useFallback ? legacy.data : undefined;
    }, [table.data, useFallback, legacy.data]);

    const error = useFallback ? legacy.error : table.error;
    return {
        data,
        error,
        isPending: error === null && data === undefined,
        refetch: () => {
            if (useFallback) legacy.refetch();
            else void table.refetch();
        },
    };
}

export interface StoreDetailState {
    data: StoreDetailResponse | undefined;
    /** La ruta de la ficha no existe todavía: la serie y el mix salen del respaldo. */
    missing: boolean;
    isError: boolean;
}

/** La ficha de la tienda (serie de 8 semanas y mix por categoría) en una llamada. */
export function useStoreDetail(storeId: string | null, period: ReportPeriod): StoreDetailState {
    const periodWindow = usePeriodWindow(period);
    const query = useQuery({
        queryKey: [...KEY, 'detail', storeId, periodWindow],
        queryFn: () => storesTableApi.getDetail(storeId ?? '', periodWindow),
        enabled: storeId !== null,
        staleTime: 60_000,
        retry: (failures, error) => !isMissingEndpoint(error) && failures < 2,
    });
    const missing = isMissingEndpoint(query.error);
    return { data: query.data, missing, isError: query.isError && !missing };
}

// RESPALDO: borrar `useLegacyStoresTable` y `useStoresTrend` cuando `/backoffice/stores/*` esté en dev.
function useLegacyStoresTable(period: ReportPeriod, enabled: boolean) {
    const periodWindow = usePeriodWindow(period);
    const windows = useWeeklyWindows();
    const activity = useQuery({ ...activityQuery(periodWindow), enabled });
    const week = useQuery({ ...activityQuery(windows[0], 5 * 60_000), enabled });
    const previousWeek = useQuery({ ...activityQuery(windows[1], 5 * 60_000), enabled });
    const { retailerId, problemError } = useRosterRetailer();
    const rosterId = enabled ? retailerId : null;
    const stores = useRosterStores(rosterId);
    const zones = useRosterZones(rosterId);
    const users = useRosterUsers(rosterId);
    const goals = useGoals('STORE', enabled);

    const data = useMemo((): StoresTableData | undefined => {
        if (!activity.data || !stores.data) return undefined;
        const goalByStore = new Map<string, number>();
        for (const goal of goals.data?.goals ?? []) {
            const cents = scaleMonthlyGoal(goal.monthCents, period);
            if (cents !== null) goalByStore.set(goal.targetId, cents);
        }
        return {
            items: buildStoreRows({
                stores: stores.data,
                zoneNames: zoneNameById(zones.data ?? []),
                period: activity.data.items,
                week: week.data?.items,
                previousWeek: previousWeek.data?.items,
                users: users.data,
                goalByStore,
            }),
            truncated: activity.data.truncated,
            teamAvailable: users.data !== undefined,
            weekAvailable: week.data !== undefined,
            source: 'fallback',
        };
    }, [
        activity.data,
        stores.data,
        zones.data,
        users.data,
        week.data,
        previousWeek.data,
        goals.data,
        period,
    ]);

    return {
        data,
        error: enabled ? (problemError ?? activity.error ?? stores.error) : null,
        refetch: () => {
            void activity.refetch();
            void stores.refetch();
            void users.refetch();
        },
    };
}

/** Serie por tienda, de la semana más vieja a la más nueva; `undefined` hasta que respondan todas. */
function combineTrend(results: { data?: StoreActivityResponse; isError: boolean }[]) {
    const isError = results.some((result) => result.isError);
    if (results.some((result) => result.data === undefined)) return { byStore: undefined, isError };
    const weeks = results.map((result) => result.data as StoreActivityResponse).reverse();
    const byStore = new Map<string, number[]>();
    weeks.forEach((week, index) => {
        for (const row of week.items) {
            const values = byStore.get(row.storeId) ?? new Array<number>(weeks.length).fill(0);
            values[index] = row.placedCents ?? 0;
            byStore.set(row.storeId, values);
        }
    });
    return { byStore, isError };
}

/**
 * Colocación semanal de cada tienda en las últimas 8 semanas. Cada semana es la misma lista de
 * tiendas con otra ventana; la tienda ausente no vendió esa semana.
 */
export function useStoresTrend(enabled: boolean) {
    const windows = useWeeklyWindows();
    return useQueries({
        queries: windows.map((kpiWindow) => ({
            ...activityQuery(kpiWindow, 5 * 60_000),
            enabled,
        })),
        combine: combineTrend,
    });
}

export interface StoreSalesResult {
    items: BackofficeSale[];
    /** Hubo más páginas de las que se leyeron. */
    truncated: boolean;
}

async function listAllStoreSales(
    retailerId: string,
    storeId: string,
    days: { from: string; to: string },
): Promise<StoreSalesResult> {
    const items: BackofficeSale[] = [];
    const seen = new Set<string>();
    let nextToken: string | undefined;
    for (let page = 0; page < MAX_SALES_PAGES; page += 1) {
        const result = await backofficeSalesApi.list(
            { retailerId, storeId, from: days.from, to: days.to },
            nextToken,
            SALES_PAGE_SIZE,
        );
        for (const sale of result.items) {
            if (seen.has(sale.saleId)) continue;
            seen.add(sale.saleId);
            items.push(sale);
        }
        // El silo omite `nextToken` en la última página: `undefined` también es fin.
        if (!result.nextToken) return { items, truncated: false };
        nextToken = result.nextToken;
    }
    return { items, truncated: true };
}

/** Ventas de la tienda en la ventana del periodo, todas las páginas hasta el tope. */
export function useStorePeriodSales(
    storeId: string | null,
    retailerId: string | null,
    period: ReportPeriod,
) {
    const today = mxDay(new Date().toISOString());
    const days = useMemo(
        () => {
            const { fromDay, toDay } = reportPeriodRange(period);
            return { from: fromDay, to: toDay };
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `today` rehace la ventana al cambiar el día
        [period, today],
    );
    return useQuery({
        queryKey: ['retail-admin', 'tiendas', 'period-sales', retailerId, storeId, days],
        queryFn: () => listAllStoreSales(retailerId ?? '', storeId ?? '', days),
        enabled: storeId !== null && retailerId !== null,
        staleTime: 60_000,
    });
}
